import type { ChannelCountersEvent } from '@/types/SocketEvents'

function isCounter(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
}

/**
 * Fronteira dos contadores vindos do socket. O front não usa Zod — validação de
 * boundary é custom, no molde do `normalize` de `services/transmission/index.ts`.
 *
 * `null` = **descartar o delta**, e só há UM motivo pra isso num payload bem-formado:
 * `waiting_chats > chats_count`. As duas somas saem do MESMO `$group` sobre o mesmo
 * `$match` no back (`_STATS_ACCUMULATORS`), então a violação não é dado ruim, é payload
 * corrompido — aplicar pintaria um número que o back não produz.
 *
 * `waiting_chats === 0 && total_unread_messages > 0` é a OUTRA impossibilidade, e ela
 * **não descarta**: significa drift de `unread_count` no back (contador positivo sem
 * mensagem correspondente), o payload descreve o estado real do banco, e descartar
 * congelaria o número na tela até o poll de 30s. Loga e aplica. Confundir as duas é o
 * erro caro deste módulo.
 */
export function parseChannelCounters(raw: unknown): ChannelCountersEvent | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null

  const { channel_id, chats_count, waiting_chats, total_unread_messages } =
    raw as Record<string, unknown>

  if (typeof channel_id !== 'string' || channel_id === '') return null
  if (!isCounter(chats_count)) return null
  if (!isCounter(waiting_chats)) return null
  if (!isCounter(total_unread_messages)) return null

  if (waiting_chats > chats_count) {
    console.warn('[counters] delta descartado: waiting_chats > chats_count', {
      channel_id,
      waiting_chats,
      chats_count,
    })
    return null
  }

  if (waiting_chats === 0 && total_unread_messages > 0) {
    console.warn('[counters] drift de unread_count no back: 0 aguardando com mensagens não lidas', {
      channel_id,
      total_unread_messages,
    })
  }

  return { channel_id, chats_count, waiting_chats, total_unread_messages }
}

/** Snapshot do `counters_info`: item inválido é descartado, o resto do snapshot passa —
 *  um canal corrompido não pode cegar os outros. */
export function parseChannelCountersList(raw: unknown): ChannelCountersEvent[] {
  if (!Array.isArray(raw)) return []
  return raw
    .map(parseChannelCounters)
    .filter((item): item is ChannelCountersEvent => item !== null)
}
